'use client'

import { Card, Cell, Label, Text } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { cookiesDataByName, useCookie } from '@marketplace-web/environment/cookies-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { urlWithParams } from '@marketplace-web/shared/url-util'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'

import { searchFaqEntriesEvent } from '@marketplace-web/help-center/help-center-faq-entry-data'
import { TrackingChannel } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import { NEW_HELP_TICKET_URL } from '../constants/routes'

type Props = {
  channel: string
  answerNotFoundId: number | null
}

const HelpCenterSearchEmptyState = ({ channel, answerNotFoundId }: Props) => {
  const translate = useTranslate('help_center')
  const { thread_id, search_text } = useBrowserNavigation().searchParams
  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  const cookies = useCookie()

  useEffect(() => {
    if (
      channel !== TrackingChannel.Search ||
      !answerNotFoundId ||
      typeof search_text !== 'string' ||
      !helpCenterSessionId
    ) {
      return
    }
    track(
      searchFaqEntriesEvent({
        searchSessionId: cookies.get(cookiesDataByName.help_center_search_session_id) || null,
        searchQuery: search_text,
        faqEntryIds: [],
        helpCenterSessionId,
      }),
    )
  }, [answerNotFoundId, channel, search_text, helpCenterSessionId, track, cookies])

  return (
    <>
      <Label text={translate('faq.questions')} />
      <Card>
        <Cell>
          <div className="empty-state">
            <div className="empty-state__content">
              <Text
                as="span"
                text={translate('faq.havent_found_a_question', {
                  contact_link: (
                    <a
                      key="contact_link"
                      href={urlWithParams(NEW_HELP_TICKET_URL, {
                        ch: TrackingChannel.Search,
                        faq_entry_id: answerNotFoundId,
                        allow_direct: 1,
                        thread_id,
                      })}
                    >
                      {translate('contact_link.title')}
                    </a>
                  ),
                })}
                theme="muted"
                alignment={Text.Alignment.Center}
              />
            </div>
          </div>
        </Cell>
      </Card>
    </>
  )
}

export default HelpCenterSearchEmptyState
